import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import dayjs from 'dayjs';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PanToolAltIcon from '@mui/icons-material/PanToolAlt';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BuildIcon from '@mui/icons-material/Build';
import SendIcon from '@mui/icons-material/Send';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';

import AuthContext from '../../context/authContext';
import useIssue from '../../hooks/useIssue';
import useUser from '../../hooks/useUser';
import { downscaleImageToBase64 } from '../../util/imageDownscale';
import {
  ISSUE_STATUSES,
  ISSUE_STATUS_BY_KEY,
  ESCALATION_STEP_LABELS,
  ESCALATION_RUN_LABELS,
  formatDurationMs,
} from '../../util/issueConstants';
import IssuePriorityChip from './IssuePriorityChip';

const formatDateTime = value => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '–');

// Default mapping of the ticket priority onto the 5-level dev todo scale,
// mirroring the backend fallback.
const DEV_PRIORITY_BY_ISSUE = { p1: 'highest', p2: 'high', p3: 'normal', p4: 'low', p5: 'lowest' };

const DEV_PRIORITY_OPTIONS = [
  { key: 'highest', label: 'Sehr hoch' },
  { key: 'high', label: 'Hoch' },
  { key: 'normal', label: 'Normal' },
  { key: 'low', label: 'Niedrig' },
  { key: 'lowest', label: 'Sehr niedrig' },
];

// Prefilled resolution note when a ticket is handed over to the developers.
const FORWARD_NOTE_DEFAULT = 'Kein IT-Mangel — als Aufgabe an die Entwicklung übergeben.';

// Compact tinted chip for an issue status (readable in both themes).
function IssueStatusChip({ status }) {
  const config = ISSUE_STATUS_BY_KEY[status];
  if (!config) return null;
  return (
    <Chip
      size='small'
      label={config.label}
      sx={{ bgcolor: alpha(config.color, 0.15), color: config.color, fontWeight: 600 }}
    />
  );
}

// Icon per escalation step delivery status.
function EscalationStepIcon({ status }) {
  if (status === 'sent') return <CheckCircleIcon fontSize='small' sx={{ color: 'success.main' }} />;
  if (status === 'failed') return <ErrorIcon fontSize='small' sx={{ color: 'error.main' }} />;
  if (status === 'skipped')
    return <SkipNextIcon fontSize='small' sx={{ color: 'text.disabled' }} />;
  return <ScheduleIcon fontSize='small' sx={{ color: 'text.disabled' }} />;
}

// Only members of the IT department may be delegated/assigned issues; the role
// names mirror the escalation chain configuration in the backend.
const IT_ROLE_NAMES = ['ITEmployee', 'ITManager', 'AssistantITManager', 'LeadEngineer'];

// User picker used for both "Reagieren & delegieren" and "Zuweisen".
function UserPickerDialog({ open, title, confirmLabel, users, onClose, onConfirm, busy }) {
  const [selectedUser, setSelectedUser] = useState(null);
  useEffect(() => {
    if (open) setSelectedUser(null);
  }, [open]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Autocomplete
          options={users}
          getOptionLabel={user => user.username || ''}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          value={selectedUser}
          onChange={(event, value) => setSelectedUser(value)}
          renderInput={params => (
            <TextField {...params} label='Mitarbeiter' autoFocus sx={{ mt: 1 }} />
          )}
          noOptionsText='Keine Mitarbeiter gefunden'
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={busy}>
          Abbrechen
        </Button>
        <Button
          variant='contained'
          disabled={!selectedUser || busy}
          onClick={() => onConfirm(selectedUser._id)}
          startIcon={busy ? <CircularProgress size={16} color='inherit' /> : null}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Detail dialog for a single issue: full data, escalation timeline, photos,
 * comments and all rights-gated quick actions. Calls onChanged after every
 * mutation so the board behind it can refresh.
 */
const IssueDetailDialog = ({ open, issueId, onClose, onChanged }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const authCtx = useContext(AuthContext);
  const {
    getIssueById,
    acknowledgeIssue,
    assignIssue,
    changeIssueStatus,
    forwardToDevTodo,
    addComment,
    uploadPhoto,
    fetchPhotoBlob,
    addToMyTodos,
  } = useIssue();
  const { fetchUsers } = useUser();

  const canManage = authCtx.checkRight('issue:manage');
  // Internal notes are an IT-only comment track: without the right they are
  // neither delivered by the backend nor offered in the composer.
  const canUseInternalNotes = authCtx.checkRight('issue:internalNote');

  const [issue, setIssue] = useState(null);
  const [escalationRun, setEscalationRun] = useState(null);
  // The acting user's personal todo linked to this issue (null = none yet).
  const [myTodo, setMyTodo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [users, setUsers] = useState([]);
  const usersLoaded = useRef(false);
  const [photoUrls, setPhotoUrls] = useState({});
  const [lightboxPhotoId, setLightboxPhotoId] = useState(null);
  // 'delegate' opens acknowledge-with-user, 'assign' the plain assignment picker.
  const [pickerMode, setPickerMode] = useState(null);
  const [resolveConfirmOpen, setResolveConfirmOpen] = useState(false);
  // "Hand over to the developers" dialog state; the priority is preselected
  // from the ticket priority when the dialog opens.
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardPriority, setForwardPriority] = useState('normal');
  const forwardTitleRef = useRef(null);
  const forwardNoteRef = useRef(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const commentRef = useRef(null);
  const [commentInternal, setCommentInternal] = useState(false);
  const resolutionNoteRef = useRef(null);
  const fileInputRef = useRef(null);
  // Object URLs of already loaded photos, kept outside state so a reload of the
  // issue does not re-download blobs that are still valid.
  const photoUrlsRef = useRef({});

  const load = useCallback(async () => {
    if (!issueId) return;
    setLoading(true);
    try {
      const result = await getIssueById(issueId);
      setIssue(result?.issue || null);
      setEscalationRun(result?.escalationRun || null);
      setMyTodo(result?.myTodo || null);
    } catch {
      // Error toast is handled inside useIssue.
    } finally {
      setLoading(false);
    }
  }, [getIssueById, issueId]);

  useEffect(() => {
    if (open && issueId) {
      setIssue(null);
      setEscalationRun(null);
      setMyTodo(null);
      // The composer always starts as a normal comment again, so an internal
      // note is never sent by accident.
      setCommentInternal(false);
      load();
    }
  }, [open, issueId, load]);

  // Identity of the current photo set. Every reload of the issue produces a new
  // object, so keying the loader on `issue` itself would re-run it after every
  // status change, comment or assignment - revoking the object URLs and
  // re-downloading unchanged blobs, which made the thumbnails flicker.
  const photoIdsKey = (issue?.photos || []).map(photo => photo._id).join(',');

  // Photos need the auth header, so each thumbnail is fetched as a blob and
  // exposed via an object URL. Already loaded photos are kept, only new ones
  // are fetched.
  useEffect(() => {
    const photoIds = photoIdsKey ? photoIdsKey.split(',') : [];
    if (!open || !issueId || photoIds.length === 0) return undefined;

    let cancelled = false;
    const loadMissing = async () => {
      const missing = photoIds.filter(photoId => !photoUrlsRef.current[photoId]);
      await Promise.all(
        missing.map(async photoId => {
          try {
            const blob = await fetchPhotoBlob(issueId, photoId);
            if (cancelled) return;
            const url = URL.createObjectURL(blob);
            photoUrlsRef.current[photoId] = url;
            setPhotoUrls(prev => ({ ...prev, [photoId]: url }));
          } catch {
            // Thumbnail stays on its placeholder; no toast for a single photo.
          }
        })
      );
    };
    loadMissing();
    return () => {
      cancelled = true;
    };
  }, [open, issueId, photoIdsKey, fetchPhotoBlob]);

  // All object URLs belong to one issue and are released together when the
  // dialog closes or switches to another issue - not on every reload.
  useEffect(
    () => () => {
      Object.values(photoUrlsRef.current).forEach(url => URL.revokeObjectURL(url));
      photoUrlsRef.current = {};
      setPhotoUrls({});
    },
    [open, issueId]
  );

  // Users for the delegate/assign pickers are loaded lazily on first use.
  // Issues are worked on by the IT department, so the pickers only offer
  // members of the IT roles (mirrors the escalation chain roles).
  const ensureUsers = useCallback(async () => {
    if (usersLoaded.current) return;
    usersLoaded.current = true;
    try {
      const result = await fetchUsers();
      const list = Array.isArray(result) ? result : result?.users || [];
      setUsers(
        list.filter(
          user =>
            user.isActive && (user.roles || []).some(role => IT_ROLE_NAMES.includes(role?.name))
        )
      );
    } catch {
      usersLoaded.current = false;
    }
  }, [fetchUsers]);

  const runMutation = useCallback(
    async (mutation, successMessage) => {
      setBusy(true);
      try {
        await mutation();
        if (successMessage) toast.success(successMessage);
        await load();
        if (onChanged) onChanged();
      } catch {
        // Error toast is handled inside useIssue.
      } finally {
        setBusy(false);
      }
    },
    [load, onChanged]
  );

  const handleAcknowledge = useCallback(
    () => runMutation(() => acknowledgeIssue(issueId), 'Issue aktualisiert'),
    [runMutation, acknowledgeIssue, issueId]
  );

  const handlePickerConfirm = useCallback(
    async userId => {
      const mode = pickerMode;
      setPickerMode(null);
      if (mode === 'delegate') {
        await runMutation(() => acknowledgeIssue(issueId, userId), 'Issue aktualisiert');
      } else if (mode === 'assign') {
        await runMutation(() => assignIssue(issueId, userId), 'Issue aktualisiert');
      }
    },
    [pickerMode, runMutation, acknowledgeIssue, assignIssue, issueId]
  );

  const handleStatusSelect = useCallback(
    newStatus => {
      if (!issue || newStatus === issue.status) return;
      if (newStatus === 'resolved') {
        setResolveConfirmOpen(true);
        return;
      }
      runMutation(() => changeIssueStatus(issueId, newStatus), 'Issue aktualisiert');
    },
    [issue, runMutation, changeIssueStatus, issueId]
  );

  const handleResolveConfirm = useCallback(async () => {
    const note = resolutionNoteRef.current?.value?.trim() || null;
    setResolveConfirmOpen(false);
    await runMutation(() => changeIssueStatus(issueId, 'resolved', note), 'Issue aktualisiert');
  }, [runMutation, changeIssueStatus, issueId]);

  const handleForwardOpen = useCallback(() => {
    if (!issue) return;
    setForwardPriority(DEV_PRIORITY_BY_ISSUE[issue.priority] || 'normal');
    setForwardOpen(true);
  }, [issue]);

  const handleForwardConfirm = useCallback(async () => {
    const title = forwardTitleRef.current?.value?.trim() || undefined;
    const resolutionNote = forwardNoteRef.current?.value?.trim() || undefined;
    setForwardOpen(false);
    await runMutation(
      () => forwardToDevTodo(issueId, { priority: forwardPriority, title, resolutionNote }),
      'Entwickler-Aufgabe erstellt und Ticket geschlossen'
    );
  }, [runMutation, forwardToDevTodo, issueId, forwardPriority]);

  const handleAddToMyTodos = useCallback(async () => {
    setBusy(true);
    try {
      const result = await addToMyTodos(issueId);
      toast.success(
        result?.alreadyExists
          ? 'Bereits in deinen persönlichen Aufgaben'
          : 'In deine persönlichen Aufgaben übernommen'
      );
      await load();
    } catch (err) {
      if (err?.code === 'TODO_TOOL_NOT_AVAILABLE' || err?.statusCode === 501) {
        toast.info('Das To-Do-Tool ist in dieser Umgebung noch nicht verfügbar');
      }
    } finally {
      setBusy(false);
    }
  }, [addToMyTodos, issueId, load]);

  const handleAddComment = useCallback(async () => {
    const text = commentRef.current?.value?.trim() || '';
    if (!text) return;
    const internal = canUseInternalNotes && commentInternal;
    setBusy(true);
    try {
      await addComment(issueId, text, internal);
      if (commentRef.current) commentRef.current.value = '';
      toast.success(internal ? 'Interne Notiz gespeichert' : 'Kommentar gespeichert');
      await load();
      if (onChanged) onChanged();
    } catch {
      // Error toast is handled inside useIssue.
    } finally {
      setBusy(false);
    }
  }, [addComment, canUseInternalNotes, commentInternal, issueId, load, onChanged]);

  const handlePhotoFiles = useCallback(
    async event => {
      const files = Array.from(event.target.files || []);
      event.target.value = '';
      if (files.length === 0) return;
      setUploading(true);
      try {
        for (const file of files) {
          const payload = await downscaleImageToBase64(file);
          await uploadPhoto(issueId, payload);
        }
        toast.success(files.length > 1 ? 'Fotos hochgeladen' : 'Foto hochgeladen');
        await load();
        if (onChanged) onChanged();
      } catch (err) {
        // Upload errors already toast in useIssue; downscale failures do not.
        if (!err?.statusCode) toast.error('Fehler beim Verarbeiten des Fotos');
      } finally {
        setUploading(false);
      }
    },
    [uploadPhoto, issueId, load, onChanged]
  );

  const locationLine = issue
    ? [
        issue.branchId?.longName || issue.branchId?.locationName,
        issue.entertainmentId?.longName || issue.entertainmentId?.entertainmentName,
        issue.resourceId?.name,
      ]
        .filter(Boolean)
        .join(' / ')
    : '';

  const assigneeLabel = issue
    ? issue.assignedUserId
      ? [issue.assignedUserId.firstName, issue.assignedUserId.lastName].filter(Boolean).join(' ') ||
        issue.assignedUserId.username
      : issue.assignedUsername
    : '';

  const lightboxPhoto = (issue?.photos || []).find(photo => photo._id === lightboxPhotoId) || null;

  const ackDurationMs =
    escalationRun?.acknowledgedAt && escalationRun?.meta?.createdAt
      ? new Date(escalationRun.acknowledgedAt).getTime() -
        new Date(escalationRun.meta.createdAt).getTime()
      : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ pr: 6 }}>
        {loading || !issue ? (
          <Skeleton width='60%' />
        ) : (
          <Box>
            <Stack direction='row' spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <IssuePriorityChip priority={issue.priority} />
              <Typography variant='h6' sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                {issue.title}
              </Typography>
              <IssueStatusChip status={issue.status} />
            </Stack>
            <Stack
              direction='row'
              spacing={1}
              sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}
              useFlexGap
            >
              {locationLine && (
                <Typography variant='body2' color='text.secondary'>
                  {locationLine}
                </Typography>
              )}
              {issue.categoryId?.name && (
                <Chip size='small' variant='outlined' label={issue.categoryId.name} />
              )}
              <Typography variant='caption' color='text.secondary'>
                Gemeldet von {issue.reportedByUsername || issue.reportedBy?.username || 'Unbekannt'}{' '}
                · {formatDateTime(issue.meta?.createdAt)}
              </Typography>
            </Stack>
          </Box>
        )}
        <IconButton
          onClick={onClose}
          size='small'
          sx={{ position: 'absolute', top: 12, right: 12 }}
          aria-label='Schließen'
        >
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading || !issue ? (
          <Stack spacing={1.5}>
            <Skeleton variant='rounded' height={48} />
            <Skeleton variant='rounded' height={96} />
            <Skeleton variant='rounded' height={140} />
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            {/* Action bar */}
            <Stack
              direction='row'
              spacing={1}
              sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
              useFlexGap
            >
              {canManage && issue.status === 'open' && (
                <>
                  <Button
                    variant='contained'
                    color='primary'
                    startIcon={
                      busy ? <CircularProgress size={16} color='inherit' /> : <PanToolAltIcon />
                    }
                    onClick={handleAcknowledge}
                    disabled={busy}
                  >
                    Ich kümmere mich
                  </Button>
                  <Button
                    variant='outlined'
                    disabled={busy}
                    onClick={() => {
                      ensureUsers();
                      setPickerMode('delegate');
                    }}
                  >
                    Reagieren &amp; delegieren…
                  </Button>
                </>
              )}
              {canManage && (
                <>
                  <FormControl size='small' sx={{ minWidth: 170 }}>
                    <InputLabel id='issue-status-select-label'>Status</InputLabel>
                    <Select
                      labelId='issue-status-select-label'
                      label='Status'
                      value={issue.status}
                      disabled={busy}
                      onChange={event => handleStatusSelect(event.target.value)}
                    >
                      {ISSUE_STATUSES.map(status => (
                        <MenuItem key={status.key} value={status.key}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant='outlined'
                    startIcon={<PersonAddAlt1Icon />}
                    disabled={busy}
                    onClick={() => {
                      ensureUsers();
                      setPickerMode('assign');
                    }}
                  >
                    Zuweisen
                  </Button>
                  {issue.status !== 'resolved' &&
                    issue.status !== 'cancelled' &&
                    !issue.forwardedDevTodoId && (
                      <Button
                        variant='outlined'
                        startIcon={<BuildIcon />}
                        disabled={busy}
                        onClick={handleForwardOpen}
                      >
                        An Entwicklung übergeben
                      </Button>
                    )}
                </>
              )}
              {issue.forwardedDevTodoId && (
                <Chip
                  size='small'
                  color='secondary'
                  variant='outlined'
                  icon={<BuildIcon />}
                  label='An Entwicklung übergeben'
                />
              )}
              {myTodo ? (
                <Chip
                  size='small'
                  color='success'
                  variant='outlined'
                  icon={<CheckCircleIcon />}
                  label={myTodo.status === 'completed' ? 'Aufgabe erledigt' : 'In deinen Aufgaben'}
                />
              ) : (
                <Button
                  variant='outlined'
                  startIcon={<PlaylistAddIcon />}
                  disabled={busy}
                  onClick={handleAddToMyTodos}
                >
                  In meine Aufgaben
                </Button>
              )}
              {assigneeLabel && (
                <Chip
                  size='small'
                  variant='outlined'
                  label={`Zugewiesen an ${assigneeLabel}`}
                  sx={{ ml: 'auto' }}
                />
              )}
            </Stack>

            {/* Description */}
            {issue.description && (
              <Box>
                <Typography variant='overline' color='text.secondary' sx={{ fontWeight: 600 }}>
                  Beschreibung
                </Typography>
                <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                  {issue.description}
                </Typography>
              </Box>
            )}

            {/* Photos */}
            <Box>
              <Stack direction='row' spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                <Typography variant='overline' color='text.secondary' sx={{ fontWeight: 600 }}>
                  Fotos
                </Typography>
                <Typography variant='caption' color='text.disabled'>
                  {(issue.photos || []).length}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button
                  size='small'
                  variant='outlined'
                  startIcon={uploading ? <CircularProgress size={14} /> : <AddPhotoAlternateIcon />}
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Foto hinzufügen
                </Button>
                <input
                  ref={fileInputRef}
                  type='file'
                  accept='image/*'
                  capture='environment'
                  multiple
                  hidden
                  onChange={handlePhotoFiles}
                />
              </Stack>
              {(issue.photos || []).length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  Keine Fotos vorhanden.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {issue.photos.map(photo => (
                    <Tooltip
                      key={photo._id}
                      title={`${photo.uploadedByUsername || ''} · ${formatDateTime(photo.uploadedAt)}`}
                    >
                      {photoUrls[photo._id] ? (
                        <Box
                          component='img'
                          src={photoUrls[photo._id]}
                          alt={photo.fileName}
                          onClick={() => setLightboxPhotoId(photo._id)}
                          sx={{
                            width: 96,
                            height: 96,
                            objectFit: 'cover',
                            borderRadius: 1.5,
                            cursor: 'pointer',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                      ) : (
                        <Skeleton variant='rounded' width={96} height={96} />
                      )}
                    </Tooltip>
                  ))}
                </Box>
              )}
            </Box>

            {/* Escalation timeline */}
            {escalationRun && (
              <Box>
                <Stack
                  direction='row'
                  spacing={1}
                  sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: 1 }}
                  useFlexGap
                >
                  <Typography variant='overline' color='text.secondary' sx={{ fontWeight: 600 }}>
                    Eskalationskette
                  </Typography>
                  <Chip
                    size='small'
                    label={ESCALATION_RUN_LABELS[escalationRun.status] || escalationRun.status}
                    color={
                      escalationRun.status === 'acknowledged'
                        ? 'success'
                        : escalationRun.status === 'running'
                          ? 'warning'
                          : 'default'
                    }
                    variant='outlined'
                  />
                  {escalationRun.status === 'acknowledged' &&
                    escalationRun.acknowledgedByUsername && (
                      <Typography variant='caption' color='text.secondary'>
                        Reagiert von {escalationRun.acknowledgedByUsername}
                        {ackDurationMs !== null && ` nach ${formatDurationMs(ackDurationMs)}`}
                      </Typography>
                    )}
                </Stack>
                <Stack spacing={0} sx={{ pl: 0.5 }}>
                  {(escalationRun.steps || []).map((step, index) => (
                    <Box key={`${step.order}-${index}`} sx={{ display: 'flex', gap: 1.25 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          pt: 0.25,
                        }}
                      >
                        <EscalationStepIcon status={step.status} />
                        {index < (escalationRun.steps || []).length - 1 && (
                          <Box sx={{ width: '2px', flex: 1, bgcolor: 'divider', my: 0.25 }} />
                        )}
                      </Box>
                      <Box sx={{ pb: 1.5, minWidth: 0 }}>
                        <Stack
                          direction='row'
                          spacing={0.75}
                          sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}
                          useFlexGap
                        >
                          <Typography variant='body2' sx={{ fontWeight: 600 }}>
                            {ESCALATION_STEP_LABELS[step.reasonTag] || step.reasonTag} –{' '}
                            {step.username || '–'}
                          </Typography>
                          {step.kind === 'sms' ? (
                            <SmsOutlinedIcon fontSize='inherit' sx={{ color: 'text.secondary' }} />
                          ) : (
                            <MailOutlineIcon fontSize='inherit' sx={{ color: 'text.secondary' }} />
                          )}
                        </Stack>
                        <Typography variant='caption' color='text.secondary'>
                          {step.status === 'sent'
                            ? `Gesendet ${formatDateTime(step.sentAt)}`
                            : `Geplant ${formatDateTime(step.scheduledFor)}`}
                        </Typography>
                        {(step.skippedReason || step.error) && (
                          <Typography
                            variant='caption'
                            sx={{
                              display: 'block',
                              color: step.error ? 'error.main' : 'text.disabled',
                            }}
                          >
                            {step.error || step.skippedReason}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Resolution note */}
            {issue.resolutionNote && (
              <Box>
                <Typography variant='overline' color='text.secondary' sx={{ fontWeight: 600 }}>
                  Lösungsnotiz
                </Typography>
                <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                  {issue.resolutionNote}
                </Typography>
                {issue.resolvedByUsername && (
                  <Typography variant='caption' color='text.secondary'>
                    Erledigt von {issue.resolvedByUsername} · {formatDateTime(issue.resolvedAt)}
                  </Typography>
                )}
              </Box>
            )}

            <Divider />

            {/* Comments */}
            <Box>
              <Typography
                variant='overline'
                color='text.secondary'
                sx={{ fontWeight: 600, display: 'block', mb: 1 }}
              >
                Kommentare
              </Typography>
              {(issue.comments || []).length === 0 ? (
                <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                  Noch keine Kommentare.
                </Typography>
              ) : (
                <Stack spacing={1.25} sx={{ mb: 1.5 }}>
                  {issue.comments.map(comment => (
                    <Box
                      key={comment._id}
                      sx={
                        comment.internal
                          ? {
                              borderRadius: 1,
                              border: theme => `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                              backgroundColor: theme => alpha(theme.palette.warning.main, 0.1),
                              px: 1,
                              py: 0.75,
                            }
                          : undefined
                      }
                    >
                      <Typography variant='caption' color='text.secondary'>
                        <Box component='span' sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {comment.username}
                        </Box>{' '}
                        · {formatDateTime(comment.createdAt)}
                        {comment.internal && (
                          <Box
                            component='span'
                            sx={{
                              ml: 0.75,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.25,
                              fontWeight: 600,
                              color: 'warning.dark',
                            }}
                          >
                            <LockIcon sx={{ fontSize: 12 }} />
                            Interne Notiz · nur für die IT sichtbar
                          </Box>
                        )}
                      </Typography>
                      <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                        {comment.text}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
              <Stack direction='row' spacing={1} sx={{ alignItems: 'flex-start' }}>
                <TextField
                  inputRef={commentRef}
                  size='small'
                  fullWidth
                  placeholder={commentInternal ? 'Interne Notiz schreiben…' : 'Kommentar schreiben…'}
                  disabled={busy}
                  sx={
                    commentInternal
                      ? {
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme => alpha(theme.palette.warning.main, 0.1),
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme => alpha(theme.palette.warning.main, 0.6),
                          },
                        }
                      : undefined
                  }
                  onKeyDown={event => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      handleAddComment();
                    }
                  }}
                />
                <Button
                  variant='contained'
                  endIcon={<SendIcon />}
                  onClick={handleAddComment}
                  disabled={busy}
                >
                  Senden
                </Button>
              </Stack>
              {canUseInternalNotes && (
                <FormControlLabel
                  sx={{ mt: 0.5 }}
                  control={
                    <Switch
                      size='small'
                      checked={commentInternal}
                      disabled={busy}
                      onChange={event => setCommentInternal(event.target.checked)}
                    />
                  }
                  label={
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <LockIcon sx={{ fontSize: 14 }} />
                      Interne Notiz (nur für die IT sichtbar)
                    </Typography>
                  }
                />
              )}
            </Box>

            {/* Status history */}
            {(issue.statusHistory || []).length > 0 && (
              <Box>
                <Box
                  onClick={() => setHistoryOpen(value => !value)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    userSelect: 'none',
                    width: 'fit-content',
                  }}
                >
                  <ExpandMoreIcon
                    fontSize='small'
                    sx={{
                      color: 'text.secondary',
                      transform: historyOpen ? 'none' : 'rotate(-90deg)',
                      transition: 'transform 0.15s',
                    }}
                  />
                  <Typography variant='overline' color='text.secondary' sx={{ fontWeight: 600 }}>
                    Statusverlauf
                  </Typography>
                  <Typography variant='caption' color='text.disabled'>
                    {issue.statusHistory.length}
                  </Typography>
                </Box>
                <Collapse in={historyOpen}>
                  <Stack spacing={0.5} sx={{ mt: 1, pl: 3 }}>
                    {issue.statusHistory.map((entry, index) => (
                      <Typography key={index} variant='caption' color='text.secondary'>
                        {ISSUE_STATUS_BY_KEY[entry.from]?.label || entry.from || 'Neu'} →{' '}
                        {ISSUE_STATUS_BY_KEY[entry.to]?.label || entry.to} · {entry.username} ·{' '}
                        {formatDateTime(entry.at)}
                      </Typography>
                    ))}
                  </Stack>
                </Collapse>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>

      {/* Lightbox */}
      <Dialog
        open={Boolean(lightboxPhoto)}
        onClose={() => setLightboxPhotoId(null)}
        maxWidth='lg'
        fullWidth
      >
        {lightboxPhoto && (
          <>
            <DialogTitle sx={{ pr: 6 }}>
              {lightboxPhoto.fileName}
              <IconButton
                onClick={() => setLightboxPhotoId(null)}
                size='small'
                sx={{ position: 'absolute', top: 12, right: 12 }}
                aria-label='Schließen'
              >
                <CloseIcon fontSize='small' />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {photoUrls[lightboxPhoto._id] ? (
                <Box
                  component='img'
                  src={photoUrls[lightboxPhoto._id]}
                  alt={lightboxPhoto.fileName}
                  sx={{ width: '100%', maxHeight: '75vh', objectFit: 'contain' }}
                />
              ) : (
                <Skeleton variant='rounded' height={320} />
              )}
              <Typography variant='caption' color='text.secondary'>
                Hochgeladen von {lightboxPhoto.uploadedByUsername || 'Unbekannt'} ·{' '}
                {formatDateTime(lightboxPhoto.uploadedAt)}
              </Typography>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Resolve confirmation with optional resolution note */}
      <Dialog
        open={resolveConfirmOpen}
        onClose={() => setResolveConfirmOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Issue als erledigt markieren</DialogTitle>
        <DialogContent>
          {/* Closing a P1 that opened an outage also puts the unit back on
              sale, so the consequence is stated before confirming. */}
          {issue?.priority === 'p1' && issue?.sourceOutageId && (
            <Alert severity='warning' sx={{ mb: 2 }}>
              Damit wird auch der Ausfall beendet: Der Bereich ist danach wieder buchbar und die
              Blocker im Buchungsraster werden entfernt.
            </Alert>
          )}
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Optional kannst du eine kurze Lösungsnotiz hinterlegen.
          </Typography>
          <TextField
            inputRef={resolutionNoteRef}
            label='Lösungsnotiz (optional)'
            multiline
            minRows={3}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setResolveConfirmOpen(false)} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='success'
            onClick={handleResolveConfirm}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} color='inherit' /> : <CheckCircleIcon />}
          >
            Erledigt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hand over to the developer board: creates a dev todo, closes the ticket */}
      <Dialog open={forwardOpen} onClose={() => setForwardOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>An die Entwicklung übergeben</DialogTitle>
        <DialogContent key={issueId || 'none'}>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Aus dem Ticket wird eine Aufgabe auf dem Entwickler-Board erstellt; das Ticket wird
            gleichzeitig mit der Lösungsnotiz unten geschlossen.
          </Typography>
          <Stack spacing={2}>
            <TextField
              inputRef={forwardTitleRef}
              label='Aufgaben-Titel'
              defaultValue={issue?.title || ''}
              fullWidth
              size='small'
              inputProps={{ maxLength: 200 }}
            />
            <FormControl size='small' fullWidth>
              <InputLabel id='forward-dev-priority-label'>Priorität der Aufgabe</InputLabel>
              <Select
                labelId='forward-dev-priority-label'
                label='Priorität der Aufgabe'
                value={forwardPriority}
                onChange={event => setForwardPriority(event.target.value)}
              >
                {DEV_PRIORITY_OPTIONS.map(option => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              inputRef={forwardNoteRef}
              label='Lösungsnotiz am Ticket'
              defaultValue={FORWARD_NOTE_DEFAULT}
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setForwardOpen(false)} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            onClick={handleForwardConfirm}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} color='inherit' /> : <BuildIcon />}
          >
            Aufgabe erstellen &amp; Ticket schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delegate / assign user picker */}
      <UserPickerDialog
        open={Boolean(pickerMode)}
        title={pickerMode === 'delegate' ? 'Reagieren & delegieren' : 'Issue zuweisen'}
        confirmLabel={pickerMode === 'delegate' ? 'Reagieren & delegieren' : 'Zuweisen'}
        users={users}
        onClose={() => setPickerMode(null)}
        onConfirm={handlePickerConfirm}
        busy={busy}
      />
    </Dialog>
  );
};

export default IssueDetailDialog;
